import { clearCookieAdvertisingIds } from '@spt-tracking/pulse-cis-sync';
import { isPromise } from '@spt-tracking/utils';
import warnOnce from '../warnOnce';
import { getFallbackConsents } from './consent-manager';
/**
 * @enum ConsentCategory
 */
export var ConsentsCategory;
(function (ConsentsCategory) {
    ConsentsCategory["CMP_ANALYTICS"] = "CMP_ANALYTICS";
    ConsentsCategory["CMP_MARKETING"] = "CMP_MARKETING";
    ConsentsCategory["CMP_ADVERTISING"] = "CMP_ADVERTISING";
    ConsentsCategory["CMP_PERSONALIZATION"] = "CMP_PERSONALIZATION";
})(ConsentsCategory || (ConsentsCategory = {}));
export const ConsentsSourceValues = ['default', 'cmp', 'cache'];
export const ConsentsPurposeCategoryStatusValues = ['accepted', 'rejected', 'unknown'];
const isLegacyConsentsPurpose = (consentsPurposeCategory) => {
    return !consentsPurposeCategory?.status;
};
const isLegacyConsents = (consents) => {
    return !!(consents &&
        !isPromise(consents) &&
        consents?.purposes &&
        Object.values(consents.purposes).every((purpose) => isLegacyConsentsPurpose(purpose)));
};
const NO_CONSENT_IN_CONFIG = 'Invalid consents passed to the Pulse SDK. "requireAdvertisingOptIn" and no "consents" config param present.';
/**
 *
 * @param consents
 * @param requireAdvertisingOptIn
 * @returns {Object} The resolved consents and whether advertising is allowed
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export async function applyAdvertisingConsent(consents, requireAdvertisingOptIn) {
    if (!consents && typeof requireAdvertisingOptIn !== 'undefined' && requireAdvertisingOptIn !== undefined) {
        warnOnce('NO_CONSENT_IN_CONFIG', NO_CONSENT_IN_CONFIG);
    }
    const includeAdvertising = await resolveIncludeAdvertising(consents, requireAdvertisingOptIn);
    if (!includeAdvertising) {
        clearCookieAdvertisingIds();
    }
    return includeAdvertising;
}
/**
 *
 * @param consents
 * @param requireAdvertisingOptIn
 * @returns {Object} The resolved consents and whether advertising is allowed
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export function applyAdvertisingConsentSync(consents, requireAdvertisingOptIn) {
    if (!consents && typeof requireAdvertisingOptIn !== 'undefined' && requireAdvertisingOptIn !== undefined) {
        warnOnce('NO_CONSENT_IN_CONFIG', NO_CONSENT_IN_CONFIG);
    }
    const includeAdvertising = resolveIncludeAdvertisingSync(consents, requireAdvertisingOptIn);
    if (!includeAdvertising) {
        clearCookieAdvertisingIds();
    }
    return includeAdvertising;
}
/**
 *
 * @param consents
 * @param requireAdvertisingOptIn
 * @returns {Boolean} whether advertising is allowed
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export async function resolveIncludeAdvertising(consents, requireAdvertisingOptIn) {
    const resolvedRequireAdvertisingOptIn = await requireAdvertisingOptIn;
    if (!resolvedRequireAdvertisingOptIn) {
        return true;
    }
    if (isPromise(consents)) {
        const resolvedConsents = await consents;
        return resolvedConsents?.purposes?.[ConsentsCategory.CMP_ADVERTISING]?.status === 'accepted';
    }
    if (isLegacyConsents(consents)) {
        return Boolean(consents?.purposes?.[ConsentsCategory.CMP_ADVERTISING]?.optIn);
    }
    return consents?.purposes?.[ConsentsCategory.CMP_ADVERTISING]?.status === 'accepted';
}
/**
 *
 * @param consents
 * @param requireAdvertisingOptIn
 * @returns {Boolean} whether advertising is allowed
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export function resolveIncludeAdvertisingSync(consents, requireAdvertisingOptIn) {
    if (!requireAdvertisingOptIn) {
        return true;
    }
    if (isLegacyConsents(consents)) {
        return Boolean(consents?.purposes?.[ConsentsCategory.CMP_ADVERTISING]?.optIn);
    }
    return consents?.purposes?.[ConsentsCategory.CMP_ADVERTISING]?.status === 'accepted';
}
export default function consents(input) {
    const { consents: consentsInput } = input || {};
    if (!consentsInput) {
        return undefined;
    }
    const purposesInput = consentsInput?.purposes ?? {};
    return {
        purposes: purposes(purposesInput),
        source: consentsInput?.source,
    };
}
/**

 * @internal

 * This can be removed when we no longer fill purposeInput.optIn: boolean based on status string.
 * isConsentsPurposes can be used to return an unmodified consent object.
 * @see https://schibsted.io.atlassian.net/browse/SDP-11907
 */
function purposes(purposesInput) {
    const purposesOutput = getFallbackConsents().purposes;
    for (const categoryInput in purposesInput) {
        const consentCategory = categoryInput;
        const purposeInput = purposesInput[consentCategory];
        const isValidCategory = Boolean(purposesOutput[consentCategory]);
        if (!purposeInput || !isValidCategory) {
            continue;
        }
        if (!isLegacyConsentsPurpose(purposeInput)) {
            purposeInput.optIn || (purposeInput.optIn = purposeInput.status === 'accepted');
        }
        purposesOutput[consentCategory] = purposeInput;
    }
    return purposesOutput;
}
